// src/api/tipoCredito.js - API para manejo de Tipos de Crédito
import { apiFetch, API_CONFIG } from './index.js';

// ==================== CONSTANTES ====================

// Estados permitidos para un Tipo de Crédito
export const ESTADOS_TIPO_CREDITO = ['activo', 'inactivo'];

// Valores por defecto
export const DEFAULT_INTERES_MENSUAL = 0;

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Convertir de snake_case (API) a camelCase (frontend) - MANTIENE AMBOS FORMATOS
 * @param {Object} apiTipoCredito - Tipo de crédito desde la API
 * @returns {Object} Tipo de crédito convertido
 */
const convertirDesdeAPI = (apiTipoCredito) => {
  if (!apiTipoCredito) return null;
  
  // Si viene en un wrapper { data: { ... } }
  let source = apiTipoCredito;
  if (apiTipoCredito.data && !Array.isArray(apiTipoCredito.data) && typeof apiTipoCredito.data === 'object') {
    source = apiTipoCredito.data;
  }
  
  return {
    // Mantener campos originales de la API (snake_case)
    idTipoCredito: source.idTipoCredito || source.id || source.id_tipo_credito,
    nombre: source.nombre,
    descripcion: source.descripcion,
    requiere_aprobacion: source.requiere_aprobacion ?? source.requiereAprobacion,
    interes_mensual: source.interes_mensual ?? source.interesMensual,
    estado: source.estado,
    
    // También agregar versión camelCase para flexibilidad
    requiereAprobacion: source.requiere_aprobacion ?? source.requiereAprobacion,
    interesMensual: source.interes_mensual ?? source.interesMensual,
    
    // Mantener datos adicionales si existen
    ...apiTipoCredito,
    ...source,
    // Asegurar normalización
    idTipoCredito: source.idTipoCredito || source.id || apiTipoCredito.idTipoCredito || apiTipoCredito.id
  };
};

/**
 * Convertir de frontend a snake_case (API)
 * @param {Object} frontendTipoCredito - Tipo de crédito desde el frontend
 * @returns {Object} Tipo de crédito para la API
 */
const convertirParaAPI = (frontendTipoCredito) => {
  return {
    nombre: frontendTipoCredito.nombre,
    descripcion: frontendTipoCredito.descripcion || '',
    requiere_aprobacion: frontendTipoCredito.requiere_aprobacion ?? 
                        frontendTipoCredito.requiereAprobacion ?? false,
    interes_mensual: frontendTipoCredito.interes_mensual ?? 
                    frontendTipoCredito.interesMensual ?? 
                    DEFAULT_INTERES_MENSUAL,
    estado: frontendTipoCredito.estado || 'activo'
  };
};

/**
 * Convertir array de tipos de crédito desde API
 * @param {Array} apiTiposCredito - Array de tipos de crédito desde API
 * @returns {Array} Array de tipos de crédito convertidos
 */
const convertirListaDesdeAPI = (apiTiposCredito) => {
  let lista = apiTiposCredito;
  if (apiTiposCredito && apiTiposCredito.data && Array.isArray(apiTiposCredito.data)) {
    lista = apiTiposCredito.data;
  }
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

/**
 * Formatear fecha de tipo de crédito (si aplica)
 * @param {string} fechaISO - Fecha en formato ISO
 * @returns {string} Fecha formateada
 */
export const formatearFecha = (fechaISO) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del tipo de crédito
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Estado booleano
 */
export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

/**
 * Formatear nombre del tipo de crédito
 * @param {string} nombre - Nombre del tipo de crédito
 * @returns {string} Nombre formateado (primera letra mayúscula)
 */
export const formatearNombre = (nombre) => {
  if (!nombre) return '';
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
};

/**
 * Validar que el interés mensual sea un número válido
 * @param {number} interes - Interés mensual a validar
 * @returns {boolean} Interés válido
 */
export const validarInteresMensual = (interes) => {
  if (interes === null || interes === undefined) return false;
  if (isNaN(interes)) return false;
  return interes >= 0 && interes <= 100;
};

/**
 * Validar formato de nombre (solo letras, números y espacios)
 * @param {string} nombre - Nombre a validar
 * @returns {boolean} Nombre válido
 */
export const validarNombre = (nombre) => {
  if (!nombre || nombre.trim() === '') return false;
  const regex = /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑ\-\.]+$/;
  return regex.test(nombre);
};

/**
 * Obtener descripción de requerimiento de aprobación
 * @param {boolean} requiereAprobacion - Si requiere aprobación
 * @returns {string} Texto descriptivo
 */
export const obtenerTextoRequiereAprobacion = (requiereAprobacion) => {
  return requiereAprobacion ? 'Sí' : 'No';
};

/**
 * Formatear interés mensual para mostrar
 * @param {number} interes - Interés mensual
 * @returns {string} Interés formateado con símbolo de porcentaje
 */
export const formatearInteres = (interes) => {
  if (interes === null || interes === undefined) return '0%';
  return `${interes}%`;
};

/**
 * Filtrar tipos de crédito por estado
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {string} estado - 'activo', 'inactivo' o 'todos'
 * @returns {Array} Tipos de crédito filtrados
 */
export const filtrarPorEstado = (tiposCredito, estado) => {
  if (!estado || estado === 'all' || estado === 'todos') return tiposCredito;
  return tiposCredito.filter(tc => tc.estado?.toLowerCase() === estado.toLowerCase());
};

/**
 * Filtrar tipos de crédito que requieren aprobación
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {boolean} requiereAprobacion - Si requiere aprobación
 * @returns {Array} Tipos de crédito filtrados
 */
export const filtrarPorAprobacion = (tiposCredito, requiereAprobacion) => {
  return tiposCredito.filter(tc => 
    (tc.requiere_aprobacion ?? tc.requiereAprobacion) === requiereAprobacion
  );
};

/**
 * Ordenar tipos de crédito por diferentes criterios
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Tipos de crédito ordenados
 */
export const ordenarTiposCredito = (tiposCredito, criterio = 'nombre') => {
  const copy = [...tiposCredito];
  
  switch (criterio) {
    case 'nombre':
      return copy.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    case 'nombre_desc':
      return copy.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
    case 'interes_mensual':
      return copy.sort((a, b) => (a.interes_mensual || 0) - (b.interes_mensual || 0));
    case 'interes_mensual_desc':
      return copy.sort((a, b) => (b.interes_mensual || 0) - (a.interes_mensual || 0));
    case 'requiere_aprobacion':
      return copy.sort((a, b) => {
        const aVal = a.requiere_aprobacion ?? a.requiereAprobacion ? 1 : 0;
        const bVal = b.requiere_aprobacion ?? b.requiereAprobacion ? 1 : 0;
        return bVal - aVal;
      });
    case 'estado':
      return copy.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
    default:
      return copy;
  }
};

/**
 * Buscar tipos de crédito por término
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Tipos de crédito filtrados
 */
export const buscarTiposCredito = (tiposCredito, termino) => {
  if (!termino || termino.trim() === '') {
    return tiposCredito;
  }
  
  const terminoLower = termino.toLowerCase().trim();
  return tiposCredito.filter(tc => 
    tc.nombre?.toLowerCase().includes(terminoLower) ||
    tc.descripcion?.toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(tc.estado)?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Obtener estadísticas de tipos de crédito
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticas = (tiposCredito) => {
  const activos = tiposCredito.filter(tc => tc.estado?.toLowerCase() === 'activo').length;
  const inactivos = tiposCredito.filter(tc => tc.estado?.toLowerCase() === 'inactivo').length;
  
  const conAprobacion = tiposCredito.filter(tc => 
    (tc.requiere_aprobacion ?? tc.requiereAprobacion) === true
  ).length;
  
  const sinAprobacion = tiposCredito.filter(tc => 
    (tc.requiere_aprobacion ?? tc.requiereAprobacion) === false
  ).length;
  
  const intereses = tiposCredito
    .filter(tc => tc.estado?.toLowerCase() === 'activo')
    .map(tc => tc.interes_mensual ?? tc.interesMensual ?? 0);
    
  const interesPromedio = intereses.length > 0 
    ? intereses.reduce((sum, i) => sum + i, 0) / intereses.length 
    : 0;
  
  const conInteres = tiposCredito.filter(tc => 
    (tc.interes_mensual ?? tc.interesMensual ?? 0) > 0
  ).length;
  
  return {
    total: tiposCredito.length,
    activos,
    inactivos,
    porcentajeActivos: tiposCredito.length > 0 ? Math.round((activos / tiposCredito.length) * 100) : 0,
    porcentajeInactivos: tiposCredito.length > 0 ? Math.round((inactivos / tiposCredito.length) * 100) : 0,
    conAprobacion,
    sinAprobacion,
    porcentajeConAprobacion: tiposCredito.length > 0 ? Math.round((conAprobacion / tiposCredito.length) * 100) : 0,
    conInteres,
    interesPromedio: Math.round(interesPromedio * 100) / 100
  };
};

// ==================== FUNCIONES API ====================

/**
 * Obtener todos los tipos de crédito
 * @returns {Promise} Lista de tipos de crédito
 */
export const obtenerTiposCredito = async () => {
  try {
    console.log('📋 Obteniendo tipos de crédito...');
    
    const response = await apiFetch('/TipoCredito');
    
    const tiposCredito = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: tiposCredito,
      count: tiposCredito.length,
      message: 'Tipos de crédito obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tipos de crédito:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tipos de crédito activos
 * @returns {Promise} Lista de tipos de crédito activos
 */
export const obtenerTiposCreditoActivos = async () => {
  try {
    console.log('📋 Obteniendo tipos de crédito activos...');
    
    const response = await apiFetch('/TipoCredito/activos');
    
    const tiposCredito = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: tiposCredito,
      count: tiposCredito.length,
      message: 'Tipos de crédito activos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tipos de crédito activos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tipos de crédito que requieren aprobación
 * @returns {Promise} Lista de tipos de crédito con aprobación requerida
 */
export const obtenerTiposCreditoConAprobacion = async () => {
  try {
    console.log('📋 Obteniendo tipos de crédito que requieren aprobación...');
    
    const response = await apiFetch('/TipoCredito/con-aprobacion');
    
    const tiposCredito = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: tiposCredito,
      count: tiposCredito.length,
      message: 'Tipos de crédito con aprobación obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tipos de crédito con aprobación:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un tipo de crédito por ID
 * @param {number} id - ID del tipo de crédito
 * @returns {Promise} Datos del tipo de crédito
 */
export const obtenerTipoCreditoPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tipo de crédito es requerido');
    }
    
    console.log(`🔍 Obteniendo tipo de crédito ID: ${id}`);
    
    const response = await apiFetch(`/TipoCredito/${id}`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Tipo de crédito obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tipo de crédito ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Crear un nuevo tipo de crédito
 * @param {Object} tipoCreditoData - Datos del tipo de crédito
 * @returns {Promise} Tipo de crédito creado
 */
export const crearTipoCredito = async (tipoCreditoData) => {
  try {
    if (!tipoCreditoData.nombre || tipoCreditoData.nombre.trim() === '') {
      throw new Error('El nombre del tipo de crédito es requerido');
    }
    
    const interes = tipoCreditoData.interes_mensual ?? 
                   tipoCreditoData.interesMensual ?? 
                   DEFAULT_INTERES_MENSUAL;
                   
    if (!validarInteresMensual(interes)) {
      throw new Error('El interés mensual debe ser un número válido (0-100)');
    }
    
    console.log('➕ Creando nuevo tipo de crédito:', tipoCreditoData);
    
    const apiData = convertirParaAPI(tipoCreditoData);
    
    const response = await apiFetch('/TipoCredito', {
      method: 'POST',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Tipo de crédito creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando tipo de crédito:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un tipo de crédito existente
 * @param {number} id - ID del tipo de crédito
 * @param {Object} tipoCreditoData - Datos a actualizar
 * @returns {Promise} Tipo de crédito actualizado
 */
export const actualizarTipoCredito = async (id, tipoCreditoData) => {
  try {
    if (!id) {
      throw new Error('ID de tipo de crédito es requerido');
    }
    
    if (!tipoCreditoData.nombre || tipoCreditoData.nombre.trim() === '') {
      throw new Error('El nombre del tipo de crédito es requerido');
    }
    
    const interes = tipoCreditoData.interes_mensual ?? 
                   tipoCreditoData.interesMensual ?? 
                   DEFAULT_INTERES_MENSUAL;
                   
    if (!validarInteresMensual(interes)) {
      throw new Error('El interés mensual debe ser un número válido (0-100)');
    }
    
    console.log(`✏️ Actualizando tipo de crédito ID: ${id}`, tipoCreditoData);
    
    const apiData = convertirParaAPI(tipoCreditoData);
    
    const response = await apiFetch(`/TipoCredito/${id}`, {
      method: 'PUT',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Tipo de crédito actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando tipo de crédito ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un tipo de crédito
 * @param {number} id - ID del tipo de crédito a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarTipoCredito = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tipo de crédito es requerido');
    }
    
    console.log(`🗑️ Eliminando tipo de crédito ID: ${id}`);
    
    const response = await apiFetch(`/TipoCredito/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: response.message || 'Tipo de crédito eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando tipo de crédito ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de un tipo de crédito
 * @param {number} id - ID del tipo de crédito
 * @param {string} nuevoEstado - Nuevo estado ('activo' o 'inactivo')
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoTipoCredito = async (id, nuevoEstado) => {
  try {
    if (!id || !nuevoEstado) {
      throw new Error('ID y nuevo estado son requeridos');
    }
    
    if (!ESTADOS_TIPO_CREDITO.includes(nuevoEstado.toLowerCase())) {
      throw new Error(`Estado inválido. Debe ser: ${ESTADOS_TIPO_CREDITO.join(', ')}`);
    }
    
    console.log(`🔄 Cambiando estado de tipo de crédito ID: ${id} a ${nuevoEstado}`);
    
    const response = await apiFetch('/TipoCredito/cambiar-estado', {
      method: 'PATCH',
      body: JSON.stringify({
        id: id,
        nuevoEstado: nuevoEstado
      })
    });
    
    return {
      success: true,
      data: response,
      message: response.message || `Estado cambiado a ${convertirEstadoATexto(nuevoEstado)} exitosamente`
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de tipo de crédito ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar un tipo de crédito
 * @param {number} id - ID del tipo de crédito
 * @returns {Promise} Resultado de la activación
 */
export const activarTipoCredito = async (id) => {
  return await cambiarEstadoTipoCredito(id, 'activo');
};

/**
 * Desactivar un tipo de crédito
 * @param {number} id - ID del tipo de crédito
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarTipoCredito = async (id) => {
  return await cambiarEstadoTipoCredito(id, 'inactivo');
};

/**
 * Validar si un nombre de tipo de crédito ya existe
 * @param {string} nombre - Nombre a validar
 * @returns {Promise} Resultado de la validación
 */
export const validarNombreTipoCredito = async (nombre) => {
  try {
    if (!nombre || nombre.trim() === '') {
      throw new Error('Nombre es requerido');
    }
    
    console.log(`🔎 Validando nombre de tipo de crédito: "${nombre}"`);
    
    const response = await apiFetch(`/TipoCredito/validar-nombre?nombre=${encodeURIComponent(nombre)}`);
    
    return {
      success: true,
      existe: response.existe || false,
      message: response.existe ? 'El nombre ya está registrado' : 'Nombre disponible'
    };
    
  } catch (error) {
    console.error(`❌ Error validando nombre de tipo de crédito "${nombre}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Obtener tipo de crédito con relaciones
 * @param {number} id - ID del tipo de crédito
 * @returns {Promise} Tipo de crédito con relaciones
 */
export const obtenerTipoCreditoConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tipo de crédito es requerido');
    }
    
    console.log(`🔍 Obteniendo tipo de crédito ID: ${id} con relaciones`);
    
    const response = await apiFetch(`/TipoCredito/${id}/conRelaciones`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Tipo de crédito con relaciones obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tipo de crédito ${id} con relaciones:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener estadísticas de un tipo de crédito específico
 * @param {number} id - ID del tipo de crédito
 * @returns {Promise} Estadísticas del tipo de crédito
 */
export const obtenerEstadisticasTipoCredito = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tipo de crédito es requerido');
    }
    
    console.log(`📊 Obteniendo estadísticas para tipo de crédito ID: ${id}`);
    
    const response = await apiFetch(`/TipoCredito/${id}/estadisticas`);
    
    return {
      success: true,
      data: response,
      message: 'Estadísticas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estadísticas para tipo de crédito ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si un tipo de crédito existe por ID
 * @param {number} id - ID a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarTipoCreditoPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID es requerido');
    }
    
    console.log(`🔎 Verificando tipo de crédito por ID: ${id}`);
    
    const response = await apiFetch(`/TipoCredito/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || false,
      message: response.exists ? 'El ID existe' : 'ID disponible'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando tipo de crédito por ID ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar tipos de crédito a PDF
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (OPCIONAL)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (tiposCredito, titulo = 'Reporte de Tipos de Crédito', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF de tipos de crédito...');
    
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!tiposCredito || tiposCredito.length === 0) {
      throw new Error('No hay tipos de crédito para exportar');
    }
    
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está instalada. Ejecuta: npm install jspdf',
        filename: null
      };
    }
    
    const doc = new jsPDF();
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const horaStr = fecha.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    let y = 20;
    const marginLeft = 20;
    
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('JEAN INFORMATICO C.A.', titleX, y);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Hora: ${horaStr}`, marginLeft + 70, y);
    doc.text(`Total: ${tiposCredito.length} tipos`, marginLeft + 140, y);
    
    y += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    
    const colPositions = {
      id: marginLeft,
      nombre: marginLeft + 20,
      requiereAprobacion: marginLeft + 80,
      interes: marginLeft + 120,
      estado: marginLeft + 150
    };
    
    doc.text('ID', colPositions.id, y);
    doc.text('Nombre', colPositions.nombre, y);
    doc.text('Req. Aprob.', colPositions.requiereAprobacion, y);
    doc.text('Interés %', colPositions.interes, y);
    doc.text('Estado', colPositions.estado, y);
    
    y += 5;
    doc.setDrawColor(200, 200, 200);
    doc.line(marginLeft, y, marginLeft + 180, y);
    
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    tiposCredito.forEach((tc, index) => {
      if (y > 270) {
        doc.addPage();
        y = 30;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('ID', colPositions.id, 20);
        doc.text('Nombre', colPositions.nombre, 20);
        doc.text('Req. Aprob.', colPositions.requiereAprobacion, 20);
        doc.text('Interés %', colPositions.interes, 20);
        doc.text('Estado', colPositions.estado, 20);
        y += 15;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
      }
      
      const idStr = String(tc.idTipoCredito || index + 1);
      const nombreStr = String(tc.nombre || 'N/A');
      const requiereStr = obtenerTextoRequiereAprobacion(tc.requiere_aprobacion ?? tc.requiereAprobacion);
      const interesStr = formatearInteres(tc.interes_mensual ?? tc.interesMensual);
      const estado = convertirEstadoATexto(tc.estado);
      const estadoStr = String(estado || 'N/A');
      
      const truncarTexto = (texto, maxLength = 25) => {
        if (texto.length > maxLength) {
          return texto.substring(0, maxLength - 3) + '...';
        }
        return texto;
      };
      
      doc.text(truncarTexto(idStr, 10), colPositions.id, y);
      doc.text(truncarTexto(nombreStr, 20), colPositions.nombre, y);
      doc.text(truncarTexto(requiereStr, 10), colPositions.requiereAprobacion, y);
      doc.text(truncarTexto(interesStr, 10), colPositions.interes, y);
      
      const estadoLower = estado.toLowerCase();
      if (estadoLower === 'activo') {
        doc.setTextColor(0, 128, 0);
      } else if (estadoLower === 'inactivo') {
        doc.setTextColor(255, 0, 0);
      } else {
        doc.setTextColor(128, 128, 128);
      }
      
      doc.text(truncarTexto(estadoStr, 10), colPositions.estado, y);
      doc.setTextColor(0, 0, 0);
      
      y += 6;
    });
    
    const pageWidth = doc.internal.pageSize.width;
    const pageCount = doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('© Jean Informatico C.A. - Sistema de Gestión de Tipos de Crédito', marginLeft, 290);
      doc.text(`Página ${i} de ${pageCount} - ${fechaStr}`, pageWidth - marginLeft, 290, null, null, 'right');
    }
    
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `tipos_credito_${fechaISO}.pdf`;
    
    doc.save(nombreArchivo);
    
    console.log('✅ PDF generado exitosamente:', nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: logoAgregado 
        ? 'PDF generado exitosamente con logo' 
        : 'PDF generado exitosamente',
      count: tiposCredito.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF de tipos de crédito:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar tipos de crédito a Excel
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (tiposCredito, filename = 'tipos_credito') => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!tiposCredito || tiposCredito.length === 0) {
      throw new Error('No hay tipos de crédito para exportar');
    }
    
    let XLSX;
    try {
      XLSX = require('xlsx');
    } catch (e) {
      console.error('❌ XLSX no está disponible:', e);
      return {
        success: false,
        error: 'La librería XLSX no está instalada. Ejecuta: npm install xlsx',
        filename: null
      };
    }
    
    const data = tiposCredito.map((tc, index) => ({
      '#': index + 1,
      'ID': tc.idTipoCredito || '',
      'Nombre': tc.nombre || '',
      'Descripción': tc.descripcion || '',
      'Requiere Aprobación': obtenerTextoRequiereAprobacion(tc.requiere_aprobacion ?? tc.requiereAprobacion),
      'Interés Mensual %': formatearInteres(tc.interes_mensual ?? tc.interesMensual),
      'Estado': convertirEstadoATexto(tc.estado)
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    
    const wscols = [
      { wch: 5 },   // #
      { wch: 8 },   // ID
      { wch: 25 },  // Nombre
      { wch: 40 },  // Descripción
      { wch: 15 },  // Requiere Aprobación
      { wch: 12 },  // Interés %
      { wch: 12 }   // Estado
    ];
    ws['!cols'] = wscols;
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TiposCredito');
    
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'Archivo Excel generado exitosamente',
      count: tiposCredito.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel tipos de crédito:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo de tipos de crédito
 * @param {Array} tiposCredito - Lista de tipos de crédito
 * @param {string} baseFilename - Nombre base
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Promise} Resultado
 */
export const exportarReporteCompleto = async (tiposCredito, baseFilename = 'reporte_completo_tipos_credito', logoBase64 = null) => {
  try {
    const excelResult = exportarExcel(tiposCredito, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const pdfResult = exportarPDF(tiposCredito, 'Reporte de Tipos de Crédito - Jean Informatico C.A.', logoBase64);
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: tiposCredito.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo de tipos de crédito:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Constantes
  ESTADOS_TIPO_CREDITO,
  DEFAULT_INTERES_MENSUAL,
  
  // Funciones API
  obtenerTiposCredito,
  obtenerTiposCreditoActivos,
  obtenerTiposCreditoConAprobacion,
  obtenerTipoCreditoPorId,
  crearTipoCredito,
  actualizarTipoCredito,
  eliminarTipoCredito,
  cambiarEstadoTipoCredito,
  activarTipoCredito,
  desactivarTipoCredito,
  obtenerTipoCreditoConRelaciones,
  obtenerEstadisticasTipoCredito,
  verificarTipoCreditoPorId,
  validarNombreTipoCredito,
  
  // Funciones auxiliares
  formatearFecha,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  formatearNombre,
  validarInteresMensual,
  validarNombre,
  obtenerTextoRequiereAprobacion,
  formatearInteres,
  filtrarPorEstado,
  filtrarPorAprobacion,
  ordenarTiposCredito,
  buscarTiposCredito,
  obtenerEstadisticas,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto,
  
  // Funciones de conversión
  convertirDesdeAPI,
  convertirParaAPI
};